import { useEffect, useMemo, useRef, useState } from "react";
import { CITY_DATA_SOURCE } from "@/data/geo/states";

/**
 * Freetext-with-suggestions city field for Settings' Hyperlocal Targeting.
 * Loads the city list for the chosen country on demand (code-split, not part
 * of the main bundle), then filters that country's list down to the chosen
 * state/province as the user types. Deliberately NOT locked to the list --
 * the dataset can't have every small town, so whatever the user types is
 * kept even if it doesn't match a suggestion.
 */
export default function CityPicker({ country, region, value, onChange, disabled, disabledPlaceholder }) {
  const [dataByState, setDataByState] = useState(null);
  const [loadedCountry, setLoadedCountry] = useState(null);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!country || !CITY_DATA_SOURCE[country]) {
      setDataByState(null);
      setLoadedCountry(null);
      return;
    }
    if (loadedCountry === country) return;
    let cancelled = false;
    CITY_DATA_SOURCE[country]().then((mod) => {
      if (!cancelled) {
        setDataByState(mod.default || mod);
        setLoadedCountry(country);
      }
    });
    return () => { cancelled = true; };
  }, [country, loadedCountry]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const cities = useMemo(
    () => (region && dataByState ? (dataByState[region] || []) : null),
    [region, dataByState]
  );
  const loadingCities = !!country && !dataByState;
  const noListForRegion = !!region && !!dataByState && cities && cities.length === 0;

  const matches = useMemo(() => {
    if (!cities || !cities.length) return [];
    const q = (value || "").trim().toLowerCase();
    const list = q ? cities.filter((c) => c.toLowerCase().includes(q)) : cities;
    return list.slice(0, 50);
  }, [cities, value]);

  const inputCls = "w-full text-sm px-3 py-2.5 border bg-[#0b1220] outline-none focus:border-[var(--accent)]";

  if (disabled) {
    return (
      <input
        disabled
        placeholder={disabledPlaceholder}
        className={`${inputCls} opacity-50 cursor-not-allowed`}
        style={{ borderColor: "var(--line)" }}
      />
    );
  }

  if (loadingCities) {
    return (
      <input
        disabled
        placeholder="Loading city list…"
        className={`${inputCls} opacity-50 cursor-not-allowed`}
        style={{ borderColor: "var(--line)" }}
      />
    );
  }

  return (
    <div className="relative" ref={wrapRef}>
      <input
        data-testid="target-city"
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={noListForRegion ? "Type your city" : "Start typing a city…"}
        className={inputCls}
        style={{ borderColor: "var(--line)" }}
        autoComplete="off"
      />
      {open && !noListForRegion && matches.length > 0 && (
        <div
          className="absolute left-0 right-0 mt-1 z-30 max-h-56 overflow-y-auto border shadow-xl"
          style={{ background: "var(--surface)", borderColor: "var(--line)" }}
        >
          {matches.map((c) => (
            <button
              key={c}
              type="button"
              data-testid="target-city-option"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onChange(c); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[#0e1626] transition-colors"
            >
              {c}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
